/**
 * Unified Configuration Manager for Glucose Diabetes Assistant
 * Consolidates all configuration sources with fallback mechanisms
 */

// Helper functions for type conversion
const parseBoolean = (value) => {
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return Boolean(value);
};

const parseInteger = (value, defaultValue = 0) => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

const parseFloatSafe = (value, defaultValue = 0.0) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Unified Configuration Object
 * Single source of truth for all application configuration
 */
const createConfig = () => {
  const config = {
    // Environment
    environment: process.env.NODE_ENV || 'development',
    
    // API Configuration - Glucose REST API
    api: {
      endpoint: process.env.REACT_APP_API_ENDPOINT || 'https://982mlzt6x6.execute-api.ap-southeast-2.amazonaws.com/prod',
      chatEndpoint: process.env.REACT_APP_CHAT_API_ENDPOINT || 'https://982mlzt6x6.execute-api.ap-southeast-2.amazonaws.com/prod',
      region: process.env.REACT_APP_API_REGION || 'ap-southeast-2',
      timeout: parseInteger(process.env.REACT_APP_API_TIMEOUT, 60000),
      retryCount: parseInteger(process.env.REACT_APP_API_RETRY_COUNT, 2),
      retryDelay: parseInteger(process.env.REACT_APP_API_RETRY_DELAY, 2000)
    },

    // AWS Cognito Configuration - Glucose User Pool
    cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'ap-southeast-2_Jwc8loRjl',
      userPoolClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID || 'cbk37cbntvgl37k0m7b9jhdh4',
      domain: process.env.REACT_APP_COGNITO_DOMAIN || 'glucose.auth.ap-southeast-2.amazoncognito.com',
      region: process.env.REACT_APP_COGNITO_REGION || 'ap-southeast-2'
    },

    // Bedrock Agent Configuration - Glucose Agent
    bedrock: {
      agentId: process.env.REACT_APP_BEDROCK_AGENT_ID || 'UHWBEBJVBH',
      agentAlias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'W5OWNQP70R',
      modelId: process.env.REACT_APP_BEDROCK_MODEL_ID || 'anthropic.claude-3-sonnet-20240229-v1:0',
      maxTokens: parseInteger(process.env.REACT_APP_BEDROCK_MAX_TOKENS, 4096),
      temperature: parseFloatSafe(process.env.REACT_APP_BEDROCK_TEMPERATURE, 0.7)
    },

    // Feature Flags
    features: {
      enableFeedback: parseBoolean(process.env.REACT_APP_ENABLE_FEEDBACK ?? 'true'),
      enableFileUpload: parseBoolean(process.env.REACT_APP_ENABLE_FILE_UPLOAD ?? 'false'),
      enableSessionRating: parseBoolean(process.env.REACT_APP_ENABLE_SESSION_RATING ?? 'true'),
      enableBugReporting: parseBoolean(process.env.REACT_APP_ENABLE_BUG_REPORTING ?? 'true'),
      enableAnalytics: parseBoolean(process.env.REACT_APP_ENABLE_ANALYTICS ?? 'true'),
      enablePerformanceMonitoring: parseBoolean(process.env.REACT_APP_ENABLE_PERFORMANCE_MONITORING ?? 'true')
    },

    // Monitoring Configuration
    monitoring: {
      enableXRay: parseBoolean(process.env.REACT_APP_ENABLE_XRAY ?? 'true'),
      logLevel: process.env.REACT_APP_LOG_LEVEL || 'info',
      enableRequestTracking: parseBoolean(process.env.REACT_APP_ENABLE_REQUEST_TRACKING ?? 'true'),
      enableErrorReporting: parseBoolean(process.env.REACT_APP_ENABLE_ERROR_REPORTING ?? 'true'),
      sampleRate: parseFloatSafe(process.env.REACT_APP_MONITORING_SAMPLE_RATE, 0.1)
    },

    // Chat Configuration
    chat: {
      maxMessageLength: parseInteger(process.env.REACT_APP_MAX_MESSAGE_LENGTH, 4000),
      minMessageLength: parseInteger(process.env.REACT_APP_MIN_MESSAGE_LENGTH, 2),
      rateLimitDelay: parseInteger(process.env.REACT_APP_RATE_LIMIT_DELAY, 1000),
      maxSessionIdLength: parseInteger(process.env.REACT_APP_MAX_SESSION_ID_LENGTH, 100),
      historyLimit: parseInteger(process.env.REACT_APP_CHAT_HISTORY_LIMIT, 100)
    },

    // Theme Configuration - Diabetes Australia Inspired
    theme: {
      primary: process.env.REACT_APP_THEME_PRIMARY || '#005EB8',
      secondary: process.env.REACT_APP_THEME_SECONDARY || '#00A9CE',
      accent: process.env.REACT_APP_THEME_ACCENT || '#F37021',
      background: process.env.REACT_APP_THEME_BACKGROUND || '#F5F7FA',
      textPrimary: process.env.REACT_APP_THEME_TEXT_PRIMARY || '#333333',
      textSecondary: process.env.REACT_APP_THEME_TEXT_SECONDARY || '#666666',
      success: '#00843D',
      fontFamily: process.env.REACT_APP_THEME_FONT_FAMILY || 'Open Sans, sans-serif'
    },

    // WebSocket Configuration - Glucose WebSocket API
    websocket: {
      url: process.env.REACT_APP_WEBSOCKET_URL || 'wss://swtgsxpr21.execute-api.ap-southeast-2.amazonaws.com/prod',
      region: process.env.REACT_APP_WEBSOCKET_REGION || 'ap-southeast-2',
      reconnectAttempts: parseInteger(process.env.REACT_APP_WEBSOCKET_RECONNECT_ATTEMPTS, 3),
      reconnectInterval: parseInteger(process.env.REACT_APP_WEBSOCKET_RECONNECT_INTERVAL, 2000),
      heartbeatInterval: parseInteger(process.env.REACT_APP_WEBSOCKET_HEARTBEAT_INTERVAL, 30000),
      connectionTimeout: parseInteger(process.env.REACT_APP_WEBSOCKET_CONNECTION_TIMEOUT, 10000),
      enabled: parseBoolean(process.env.REACT_APP_WEBSOCKET_ENABLED ?? 'true'),
      authEnabled: parseBoolean(process.env.REACT_APP_WEBSOCKET_AUTH_ENABLED ?? 'true'),
      authHeaderName: process.env.REACT_APP_WEBSOCKET_AUTH_HEADER || 'Authorization',
      streaming: {
        enabled: parseBoolean(process.env.REACT_APP_WEBSOCKET_STREAMING_ENABLED ?? 'true'),
        chunkProcessing: parseBoolean(process.env.REACT_APP_WEBSOCKET_CHUNK_PROCESSING ?? 'true'),
        showTypingIndicator: parseBoolean(process.env.REACT_APP_WEBSOCKET_SHOW_TYPING ?? 'true'),
        minDisplayTime: parseInteger(process.env.REACT_APP_WEBSOCKET_MIN_DISPLAY_TIME, 500),
        maxChunkDelay: parseInteger(process.env.REACT_APP_WEBSOCKET_MAX_CHUNK_DELAY, 100),
        autoComplete: parseBoolean(process.env.REACT_APP_WEBSOCKET_AUTO_COMPLETE ?? 'true')
      }
    },

    // Performance Configuration
    performance: {
      bundleOptimization: parseBoolean(process.env.REACT_APP_BUNDLE_OPTIMIZATION ?? 'true'),
      lazyLoading: parseBoolean(process.env.REACT_APP_LAZY_LOADING ?? 'true'),
      cacheMaxAge: parseInteger(process.env.REACT_APP_CACHE_MAX_AGE, 300000),
      preloadRoutes: parseBoolean(process.env.REACT_APP_PRELOAD_ROUTES ?? 'false')
    }
  };

  return config;
};

/**
 * Configuration validation
 */
const validateConfig = (config) => {
  const errors = [];
  const warnings = [];

  if (!config.api.endpoint) {
    errors.push('API endpoint is required');
  } else if (!config.api.endpoint.startsWith('https://')) {
    warnings.push('API endpoint should use HTTPS in production');
  }

  if (!config.cognito.userPoolId) {
    errors.push('Cognito User Pool ID is required');
  }
  if (!config.cognito.userPoolClientId) {
    errors.push('Cognito User Pool Client ID is required');
  }

  if (!config.bedrock.agentId) {
    errors.push('Bedrock Agent ID is required');
  }
  if (!config.bedrock.agentAlias) {
    errors.push('Bedrock Agent Alias is required');
  }

  if (config.chat.maxMessageLength < config.chat.minMessageLength) {
    errors.push('Max message length must be greater than min message length');
  }

  if (warnings.length > 0) {
    console.warn('Configuration warnings:', warnings);
  }

  if (errors.length > 0) {
    console.error('Configuration validation failed:', errors);
    throw new Error(`Configuration validation failed: ${errors.join(', ')}`);
  }

  return true;
};

/**
 * Get Amplify configuration
 */
const getAmplifyConfig = (config) => {
  return {
    Auth: {
      Cognito: {
        userPoolId: config.cognito.userPoolId,
        userPoolClientId: config.cognito.userPoolClientId,
        region: config.cognito.region,
        loginWith: {
          oauth: {
            domain: config.cognito.domain,
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: [
              'http://localhost:3000/',
              'https://di6dnn80vs5jo.cloudfront.net/'
            ],
            redirectSignOut: [
              'http://localhost:3000/',
              'https://di6dnn80vs5jo.cloudfront.net/'
            ],
            responseType: 'code'
          }
        }
      }
    },
    API: {
      REST: {
        GlucoseAPI: {
          endpoint: config.api.endpoint,
          region: config.api.region
        }
      }
    }
  };
};

/**
 * Initialize configuration
 */
const initializeConfig = () => {
  try {
    const config = createConfig();
    validateConfig(config);
    return config;
  } catch (error) {
    console.error('Configuration initialization failed:', error);
    throw error;
  }
};

// Initialize configuration immediately
const appConfig = initializeConfig();

// Export configuration sections
export const getApiConfig = () => appConfig.api;
export const getCognitoConfig = () => appConfig.cognito;
export const getBedrockConfig = () => appConfig.bedrock;
export const getFeatureFlags = () => appConfig.features;
export const getMonitoringConfig = () => appConfig.monitoring;
export const getChatConfig = () => appConfig.chat;
export const getThemeConfig = () => appConfig.theme;
export const getWebSocketConfig = () => appConfig.websocket;
export const getPerformanceConfig = () => appConfig.performance;

// Export full configuration
export const getConfig = () => appConfig;

// Export Amplify configuration
export const getAmplifyConfiguration = () => getAmplifyConfig(appConfig);

// Export initialization function for dynamic reconfiguration
export { initializeConfig };

// Default export
export default {
  config: appConfig,
  getApiConfig,
  getCognitoConfig,
  getBedrockConfig,
  getFeatureFlags,
  getMonitoringConfig,
  getChatConfig,
  getThemeConfig,
  getPerformanceConfig,
  getConfig,
  getAmplifyConfiguration,
  initializeConfig
};
